/**
 * Named password policies that replace the per-tenant `createOrChangePasswordReqsField` functions.
 *
 * Each tenant's config.json specifies a policy name and min length:
 *   { "passwordPolicy": "default", "createOrChangePasswordMinLengthValue": 8 }
 *
 * The frontend resolves the policy name to the actual validation rules via this module.
 */

// Réplica EXACTA de la lógica de swarm (variants/macro/config/tenantConfig.js,
// post-DEL-6280) para macroStrict. La inicialización del contador (pre-chequeo de
// la posición 1) hace que ya detecte 3 ascendentes ("abc") — el label dice "(abc)".
// Mantener idéntico a swarm para paridad display↔submit.
const macroNoConsecutiveOrRepeating = (value) => {
  if (!value) return false;
  if (value.match(/(\W|\w)(\1){2}/i)) return false; // sin 3 repetidos (aaa)
  const containsConsecutive = (str, length) => {
    let count = 0;
    let current = str.charCodeAt(0);
    const first = str.charCodeAt(1);
    if (first - current === 1) count = 1;
    for (let i = 1; i < str.length; i++) {
      const next = str.charCodeAt(i);
      if (next - current === 1) count++;
      else count = 0;
      current = next;
      if (count >= length - 1) return true;
    }
    return false;
  };
  return !(
    containsConsecutive(value, 4) ||
    containsConsecutive(value.split('').reverse().join(''), 4)
  );
};

const macroNoRepeatedPermutations = (value, tolerance = 4) => {
  if (!value) return false;
  const perms = [];
  for (let i = 0; i + tolerance <= value.length; i++) {
    perms.push(value.slice(i, i + tolerance));
  }
  for (const perm of perms) {
    const escaped = perm.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    if (value.match(new RegExp(escaped, 'g')).length > 1) return false;
  }
  return true;
};

const policies = {
  // Default: min length + any letter + any number
  // Used by: default, tacataca, compraqui, bn, newpartners, santander, firstdata, bendo, payscan, mio, xpay
  default: (minLength) => [
    {
      test: (value) => value && value.length >= minLength,
      label: 'resetPassword.form.userPassword.requirements.minLength',
    },
    {
      test: (value) => value && value.match(/[a-z]/i),
      label: 'resetPassword.form.userPassword.requirements.anyLetter',
    },
    {
      test: (value) => value && value.match(/\d/i),
      label: 'resetPassword.form.userPassword.requirements.anyNumber',
    },
  ],

  // Default + no spaces at beginning/end
  // Used by: bajio, uala-mx, itau-py, banco-de-chile
  defaultNoSpaces: (minLength) => [
    {
      test: (value) => value && value.length >= minLength,
      label: 'resetPassword.form.userPassword.requirements.minLength',
    },
    {
      test: (value) => value && value.match(/[a-z]/i),
      label: 'resetPassword.form.userPassword.requirements.anyLetter',
    },
    {
      test: (value) => value && value.match(/\d/i),
      label: 'resetPassword.form.userPassword.requirements.anyNumber',
    },
    {
      test: (value) => value && value.match(/^[^\s]+(\s+[^\s]+)*$/),
      label: 'resetPassword.form.userPassword.requirements.noEmptySpacesAtBeginnigAndEnd',
    },
  ],

  // Strict: lowercase + uppercase + number + special char
  // Used by: wapa, cabal
  strictSpecial: (minLength) => [
    {
      test: (value) => value && value.length >= minLength,
      label: 'resetPassword.form.userPassword.requirements.minLength',
    },
    {
      test: (value) => value && value.match(/[a-z]/),
      label: 'resetPassword.form.userPassword.requirements.lowcase',
    },
    {
      test: (value) => value && value.match(/[A-Z]/),
      label: 'resetPassword.form.userPassword.requirements.upcase',
    },
    {
      test: (value) =>
        value && value.match(/[!@#$%^&*()_+|~\-\s=\\`{}[\]:";'<>?,./]/),
      label: 'resetPassword.form.userPassword.requirements.specialChar',
    },
    {
      test: (value) => value && value.match(/\d/i),
      label: 'resetPassword.form.userPassword.requirements.anyNumber',
    },
  ],

  // Strict with different labels: uppercase + lowercase + number + special char
  // Used by: openpay-ar, lulopay
  strictSpecialAlt: (minLength) => [
    {
      test: (value) => value && value.match(/[a-z]/),
      label: 'resetPassword.form.userPassword.requirements.uppercaseLetter',
    },
    {
      test: (value) => value && value.match(/[A-Z]/),
      label: 'resetPassword.form.userPassword.requirements.lowercaseLetter',
    },
    {
      test: (value) => value && value.match(/\d/i),
      label: 'resetPassword.form.userPassword.requirements.anyNumber',
    },
    {
      test: (value) =>
        value && value.match(/[.@#%*/()+\-?!:;,_]/),
      label: 'resetPassword.form.userPassword.requirements.specialCharacter',
    },
    {
      test: (value) => value && value.length >= minLength,
      label: 'resetPassword.form.userPassword.requirements.minLength',
    },
  ],

  // Macro: strict + no consecutive/repeating chars + no permutations
  // Used by: macro
  macroStrict: (minLength) => [
    // Tests envueltos en Boolean(...) como swarm: con el campo vacío devuelven
    // `false` (no `undefined`), así estos requisitos se marcan INVÁLIDOS (✗) desde
    // el arranque, igual que swarm. Los informativos (más abajo) siguen devolviendo
    // `undefined` a propósito → quedan neutros (●).
    {
      test: (value) => Boolean(value && value.length >= minLength),
      label: 'resetPassword.form.userPassword.requirements.minLength',
    },
    {
      test: (value) => Boolean(value && value.match(/[a-z]/) && value.match(/[A-Z]/)),
      label: 'resetPassword.form.userPassword.requirements.upperAndLowerCase',
    },
    {
      test: (value) => Boolean(value && value.match(/\d/i)),
      label: 'resetPassword.form.userPassword.requirements.anyNumber',
    },
    {
      test: (value) => Boolean(value && value.match(/[.@#%*/()+\-?!:;,_]/)),
      label: 'resetPassword.form.userPassword.requirements.specialCharacter',
    },
    {
      test: macroNoConsecutiveOrRepeating,
      label: 'resetPassword.form.userPassword.requirements.consecutiveAscDecOrRepeatingCharacters',
    },
    {
      test: macroNoRepeatedPermutations,
      label: 'resetPassword.form.userPassword.requirements.permutations',
    },
    // Informativas (DEL-6280): se muestran como requisito pero se validan
    // server-side. test => undefined las deja neutras (ni válidas ni inválidas).
    {
      test: (value) => undefined,
      label: 'resetPassword.form.userPassword.requirements.personalInformation',
    },
    {
      test: (value) => undefined,
      label: 'resetPassword.form.userPassword.requirements.usedPassword',
    },
  ],
};

/**
 * Named submit validators for password forms.
 * Each tenant's config.json specifies which submit policy to use.
 */
const submitPolicies = {
  // Default: min 8, any letter, any number
  default: [
    {
      field: 'password',
      test: (val) =>
        val && val.length >= 8 && val.match(/[a-z]/i) && val.match(/\d/i),
    },
  ],

  // Strict: min 12, lowercase, uppercase, special char, number
  // Used by: wapa, cabal
  strictSpecial12: [
    {
      field: 'password',
      test: (value) =>
        value &&
        value.length >= 12 &&
        value.match(/[a-z]/) &&
        value.match(/[A-Z]/) &&
        value.match(/[^a-zA-Z0-9]/) &&
        value.match(/\d/i),
    },
  ],

  // Strict alt: min 12, lowercase, uppercase, special char, number (same rules, used by different tenants)
  // Used by: openpay-ar, lulopay
  strictSpecialAlt12: [
    {
      field: 'password',
      test: (val) =>
        val &&
        val.length >= 12 &&
        /[a-z]/.test(val) &&
        val.match(/\d/i) &&
        /[A-Z]/.test(val) &&
        /[^A-Za-z0-9]/.test(val),
    },
  ],

  // Default + noEmptySpaces validator
  // Used by: bajio, uala-mx, itau-py, banco-de-chile, openpay-ar, lulopay
  defaultNoSpaces: [
    {
      field: 'password',
      noEmptySpacesAtBeginningAndEnd: (val) => val,
      test: (val) =>
        val && val.length >= 8 && val.match(/[a-z]/i) && val.match(/\d/i),
    },
  ],

  // Macro: las 6 reglas de swarm (passwordValidations), validadas por separado —
  // igual que el display. Antes era 1 regla colapsada que NO validaba consecutivos
  // ni permutaciones (y exigía minús+mayús). Se alinea a swarm-latest (DEL-6280).
  macroStrict: [
    { field: 'password', test: (val) => val && val.length >= 12 },
    { field: 'password', test: (val) => val && val.match(/[a-z]/) && val.match(/[A-Z]/) },
    { field: 'password', test: (val) => val && val.match(/\d/i) },
    {
      field: 'password',
      test: (val) => val && val.match(/[.@#%*/()+\-?!:;,_]/),
    },
    { field: 'password', test: macroNoConsecutiveOrRepeating },
    { field: 'password', test: macroNoRepeatedPermutations },
  ],
};

/**
 * Get password field requirements by policy name.
 *
 * @param {string} policyName - The policy name from config.json (e.g., "default", "strictSpecial")
 * @param {number} minLength - The minimum password length
 * @returns {Array} Array of requirement objects with `test` and `label`
 */
export const getPasswordRequirements = (policyName, minLength) => {
  const policy = policies[policyName] || policies.default;
  return policy(minLength);
};

/**
 * Get password submit validators by policy name.
 *
 * @param {string} policyName - The submit policy name from config.json
 * @returns {Array} Array of validator objects for validateFields()
 */
export const getPasswordSubmitValidators = (policyName) => {
  return submitPolicies[policyName] || submitPolicies.default;
};
